import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import shopIndentsApi from '../../services/shopIndentsApi';

// Keep the pay-link flow in place for rollout later; hide this unavailable UI.
const CUSTOMER_UPI_PAYMENT_ENABLED = false;

const statusLabel = (status) => {
  switch (status) {
    case 'pending':
      return 'Waiting for shop';
    case 'quoted':
      return 'Bill ready — pay';
    case 'awaiting_payment':
      return 'Payment pending confirm';
    case 'paid':
      return 'Paid — preparing';
    case 'ready':
      return 'Ready for handover';
    case 'delivered':
    case 'completed':
      return 'Delivered';
    case 'declined':
      return 'Shop declined';
    case 'customer_declined':
      return 'You declined';
    case 'customer_cancelled':
      return 'You cancelled';
    default:
      return status || '—';
  }
};

const statusColor = (status) => {
  if (status === 'pending') return 'warning';
  if (status === 'quoted' || status === 'awaiting_payment') return 'info';
  if (status === 'paid' || status === 'ready') return 'success';
  if (String(status || '').includes('declined') || String(status || '').includes('cancelled')) return 'error';
  return 'default';
};

/**
 * Citizen Order review — parity with Flutter MyShopRequestsScreen.
 */
const UserShopOrders = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [payNoteOpen, setPayNoteOpen] = useState(null);
  const [payNote, setPayNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await shopIndentsApi.mine();
      setRows(data);
    } catch (e) {
      setError(e?.message || 'Could not load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'My shop orders | LOCSY';
    load();
  }, [load]);

  const resend = async (row) => {
    try {
      await shopIndentsApi.resendAlert(row.id);
      toast.success('Reminder sent — same order, no new request');
      load();
    } catch (e) {
      toast.error(e?.message || 'Resend failed');
    }
  };

  const decline = async (row) => {
    try {
      await shopIndentsApi.customerRespond(row.id, 'decline');
      toast.info('You declined this bill');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed');
    }
  };

  const openPay = async (row) => {
    try {
      if (row.status === 'quoted') {
        await shopIndentsApi.customerRespond(row.id, 'pay');
      }
      const url = shopIndentsApi.publicPayUrl(row.id);
      window.open(url, '_blank', 'noopener,noreferrer');
      load();
    } catch (e) {
      toast.error(e?.message || 'Could not open pay link');
    }
  };

  const claimPaid = async () => {
    if (!payNoteOpen?.id) return;
    try {
      await shopIndentsApi.customerRespond(
        payNoteOpen.id,
        'claim_paid',
        payNote.trim(),
      );
      toast.success('Shop notified — they will confirm payment');
      setPayNoteOpen(null);
      setPayNote('');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed');
    }
  };

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Order review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Orders you sent to shops (coupons + catalog). Edit/resend keeps the same
            order id — no duplicate for Mitra.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to="/user/coupons" variant="outlined">
            Coupons
          </Button>
          <Button onClick={load} variant="contained" sx={{ bgcolor: '#FF6A00' }}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
        Coupons apply when the shop confirms payment. Locsy does not guarantee Mitra
        offers — shops may accept, edit, or decline.
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      ) : rows.length === 0 ? (
        <Alert severity="warning">
          No orders yet. Open Coupons → Redeem to send an order to the shop.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((row) => {
            const status = row.status || '';
            const items = Array.isArray(row.items) ? row.items : [];
            const names = items
              .map((it) => (it && it.name ? it.name : null))
              .filter(Boolean)
              .join(', ');
            const amount =
              row.finalAmountInr != null
                ? Number(row.finalAmountInr)
                : Number(row.subtotalInr || 0);
            return (
              <Card key={row.id} variant="outlined">
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    <Typography fontWeight={800}>
                      {row.businessName || 'Shop'}
                    </Typography>
                    <Chip
                      size="small"
                      color={statusColor(status)}
                      label={statusLabel(status)}
                    />
                  </Stack>
                  {row.tokenNo || row.payQNo ? (
                    <Typography variant="caption" color="primary" fontWeight={700}>
                      {row.tokenNo
                        ? `Token ${row.tokenNo}`
                        : `Pay Q ${row.payQNo}`}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {names || 'Items'}
                  </Typography>
                  <Typography fontWeight={700} sx={{ mt: 0.5 }}>
                    {row.finalAmountInr != null
                      ? `Pay ₹${amount.toFixed(0)}`
                      : `Est. ₹${amount.toFixed(0)}`}
                  </Typography>

                  {status === 'pending' && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Button size="small" variant="outlined" onClick={() => resend(row)}>
                        Resend
                      </Button>
                    </Stack>
                  )}

                  {(status === 'quoted' || status === 'awaiting_payment') && (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mt: 1.5 }}
                    >
                      {status === 'quoted' && (
                        <Button size="small" variant="outlined" onClick={() => decline(row)}>
                          Decline
                        </Button>
                      )}
                      {CUSTOMER_UPI_PAYMENT_ENABLED && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => openPay(row)}
                          sx={{ bgcolor: '#FF6A00' }}
                        >
                          Pay (UPI)
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setPayNoteOpen(row);
                          setPayNote('');
                        }}
                      >
                        I&apos;ve paid — notify shop
                      </Button>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={!!payNoteOpen} onClose={() => setPayNoteOpen(null)} fullWidth maxWidth="xs">
        <DialogTitle>I&apos;ve paid</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            label="Optional note (UPI ref / time)"
            value={payNote}
            onChange={(e) => setPayNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayNoteOpen(null)}>Cancel</Button>
          <Button variant="contained" onClick={claimPaid}>
            Notify shop
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserShopOrders;
